import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Checkbox, IconButton, Modal } from "@mui/material"
import { LobbyAvailableFilters } from "api"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import {
  ArrowDown,
  CaseIcon,
  CheckboxIcon,
  CheckedCheckboxIcon,
  LocationIcon,
  NewStrokeIcon,
  NewText,
  XIcon,
} from "ui"
import { jobFilter } from "utils"
import { CareerLobbyMessages } from "../Careers"
import { FilterState } from "./Jobs"

const StyledModal = styled(Modal)`
  z-index: 10002 !important;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  background-color: ${(props) => props.theme.colors.white};
  width: 100%;
  height: 90%;
  max-height: 800px;
  ${(props) => props.theme.breakpoints.up("md")} {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: auto;
    max-height: 80vh;
    border-radius: 16px;
  }
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  font-family: var(--narkis) !important;
  & * {
    font-family: inherit !important;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 16px;
  position: relative;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const StyledTitleText = styled(NewText)`
  font-family: var(--narkis-condensed) !important;
  line-height: 48px !important;
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 16px;
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  width: 40px;
  height: 40px;
  &:hover {
    background-color: ${(props) => props.theme.colors.lightGrey};
  }
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  padding: 16px;
  display: flex;
  gap: 16px;
  box-shadow: 0px -4px 12px 0px rgba(0, 0, 0, 0.05); /* Soft shadow for top */
  background: ${(props) => props.theme.colors.white};
`

const FooterButton = styled.button<{ variant?: "primary" | "secondary"; minWidth?: string }>`
  flex: 1;
  padding: 9px 0;
  border-radius: 8px;
  font-family: "NarkissBlock", sans-serif;
  font-weight: 700;
  font-size: 18px;
  height: 40px;
  min-width: ${(props) => props.minWidth || "auto"};
  border: ${(props) =>
    props.variant === "secondary"
      ? `1px solid ${props.theme.colors.darkBlue4rentMarketing}`
      : "none"};
  background-color: ${(props) => {
    if (props.variant === "secondary") return props.theme.colors.white
    return props.disabled
      ? props.theme.colors.gray4rentMarketing
      : props.theme.colors.blue4rentMarketing
  }};
  color: ${(props) =>
    props.variant === "secondary"
      ? props.theme.colors.darkBlue4rentMarketing
      : props.theme.colors.white};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`

const AccordionItem = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
`

const AccordionHeader = styled.button`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  min-height: 60px;
  width: 100%;
  border: none;
  background-color: transparent;
  font-family: inherit;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  width: 24px;
  height: 24px;
`

const HeaderRight = styled.div`
  display: flex;
  direction: ltr;
  align-items: center;
  gap: 8px;
  flex-direction: row-reverse;
`

const StyledLabelText = styled(NewText)`
  line-height: 24px !important;
`

const StyledFilterOptionText = styled(NewText)`
  line-height: 24px !important;
`

const HeaderText = styled.div`
  flex: 1;
  text-align: left;
  padding: 0 16px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AccordionBody = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.white};
  padding: 8px 16px;
`

const FilterRow = styled.label<{ isSelected: boolean }>`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  padding: 12px 8px;
  background-color: ${(props) =>
    props.isSelected ? props.theme.colors.lightGrey : props.theme.colors.white};
  cursor: pointer;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
`

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  width: 20px;
  height: 20px;
  min-width: 20px;

  .MuiSvgIcon-root {
    width: 20px;
    height: 20px;
    overflow: visible;
  }

  &.Mui-checked {
    color: ${(props) => props.theme.colors.blue4rentMarketing};
  }
`

const StyledLocationIcon = styled(LocationIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${(props) => props.$color};
  }
`

const StyledCompanyIcon = styled(NewStrokeIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    stroke: ${(props) => props.$color};
  }
`

const StyledScopeIcon = styled(CaseIcon, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>`
  width: 16px;
  height: 16px;

  path {
    stroke: ${(props) => props.$color};
    stroke-opacity: 1;
  }
`

type AccordionFilterProps = {
  label: string
  filterKey: keyof FilterState
  filters: LobbyAvailableFilters[keyof LobbyAvailableFilters]
  state: FilterState
  setState: Dispatch<SetStateAction<FilterState>>
  icon?: React.ReactNode
}

function AccordionFilter({
  label,
  icon,
  filterKey,
  filters,
  state,
  setState,
}: AccordionFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [maxLength, setMaxLength] = useState(20)
  const theme = useTheme()

  const calculateMaxLength = useCallback(() => {
    // Calculate based on screen width - more reliable on mobile
    // Modal takes ~100% width on mobile, HeaderText takes about 40% of that
    // At 14px font, each character is roughly 7-8px wide
    const screenWidth = window.innerWidth
    const availableWidth = screenWidth * 0.4
    const charsPerPixel = availableWidth / 8
    setMaxLength(Math.max(10, Math.floor(charsPerPixel)))
  }, [])

  useEffect(() => {
    calculateMaxLength()
    window.addEventListener("resize", calculateMaxLength)
    return () => window.removeEventListener("resize", calculateMaxLength)
  }, [calculateMaxLength])

  const selectedIds = state[filterKey] as number[]
  const selectedItems = filters.filter((f) => selectedIds.includes(f.id))
  const selectedNames = selectedItems.map((f) => f.name).join(" | ")

  const allSelected = selectedIds.length === filters.length || selectedIds.length === 0
  const allLabelId = `${filterKey}-all-label`

  const toggleOpen = () => setIsOpen(!isOpen)

  const handleSelect = (id: number) => {
    setState((prev) => {
      const current = prev[filterKey] as number[]
      if (current.includes(id)) {
        return { ...prev, [filterKey]: current.filter((x) => x !== id) }
      } else {
        return { ...prev, [filterKey]: [...current, id] }
      }
    })
  }

  const handleSelectAll = () => {
    setState((prev) => ({
      ...prev,
      [filterKey]: [],
    }))
  }

  return (
    <AccordionItem>
      <AccordionHeader onClick={toggleOpen} type="button" aria-expanded={isOpen}>
        <HeaderLeft>
          <div
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }}
          >
            <ArrowDown />
          </div>
        </HeaderLeft>
        <HeaderText>
          <NewText
            component="span"
            desktopFontSize={14}
            mobileFontSize={14}
            color={theme.colors.newBlack}
            title={selectedNames}
            style={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "clip",
              maxWidth: "100%",
            }}
          >
            {selectedNames.length > maxLength
              ? `${selectedNames.slice(0, maxLength)}...`
              : selectedNames}
          </NewText>
        </HeaderText>
        <HeaderRight>
          <StyledLabelText
            component="span"
            desktopFontSize={18}
            mobileFontSize={18}
            desktopFontWeight={isOpen || !allSelected ? 700 : 400}
            color={theme.colors.newBlack}
          >
            {label}
          </StyledLabelText>
          <div
            style={{
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </div>
        </HeaderRight>
      </AccordionHeader>
      <AccordionBody isOpen={isOpen}>
        <FilterRow isSelected={allSelected}>
          <StyledCheckbox
            checked={allSelected}
            onChange={handleSelectAll}
            checkedIcon={<CheckedCheckboxIcon color={theme.colors.blue4rentMarketing} />}
            icon={<CheckboxIcon size={20} />}
            inputProps={{ "aria-labelledby": allLabelId }}
          />
          <StyledFilterOptionText
            component="span"
            desktopFontSize={16}
            mobileFontSize={16}
            color={theme.colors.newBlack}
            mobileFontWeight={300}
          >
            {filterKey === "areaIds"
              ? "כל האזורים"
              : filterKey === "companyIds"
              ? "כל החברות"
              : filterKey === "professionIds"
              ? "כל התחומים"
              : "הכל"}
          </StyledFilterOptionText>
        </FilterRow>
        {filters.map((item) => {
          const isSelected = selectedIds.includes(item.id)
          return (
            <FilterRow key={item.id} isSelected={isSelected && !allSelected}>
              <StyledCheckbox
                checked={isSelected && !allSelected}
                onChange={() => handleSelect(item.id)}
                checkedIcon={<CheckedCheckboxIcon color={theme.colors.blue4rentMarketing} />}
                icon={<CheckboxIcon size={20} />}
              />
              <StyledFilterOptionText
                component="span"
                desktopFontSize={16}
                mobileFontSize={16}
                color={theme.colors.newBlack}
                mobileFontWeight={300}
              >
                {item.name}
              </StyledFilterOptionText>
            </FilterRow>
          )
        })}
      </AccordionBody>
    </AccordionItem>
  )
}

type Props = {
  open: boolean
  handleClose: () => void
  filters: LobbyAvailableFilters
  state: FilterState
  setState: Dispatch<SetStateAction<FilterState>>
  setInterimState: Dispatch<SetStateAction<FilterState>>
  interimState: FilterState
  messages: CareerLobbyMessages
  clearAll: () => void
  companyDivisionId: number[]
}

const arraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort((x, y) => x - y)
  const sortedB = [...b].sort((x, y) => x - y)
  return sortedA.every((value, idx) => value === sortedB[idx])
}

const comparableKeys: (keyof Pick<
  FilterState,
  "areaIds" | "companyDivisions" | "companyIds" | "jobScopeIds" | "orderIds" | "professionIds"
>)[] = ["areaIds", "companyDivisions", "companyIds", "jobScopeIds", "orderIds", "professionIds"]

const filtersEqual = (a: FilterState, b: FilterState) =>
  comparableKeys.every((key) => arraysEqual(a[key] as number[], b[key] as number[])) &&
  (a.searchText?.trim() ?? "") === (b.searchText?.trim() ?? "")

export default function FiltersModal({
  handleClose,
  open,
  filters,
  setState,
  state,
  interimState,
  setInterimState,
  messages,
  clearAll,
  companyDivisionId,
}: Props) {
  const theme = useTheme()

  const hasChanges = !filtersEqual(interimState, state)

  const applyFilters = () => {
    if (!hasChanges) return
    jobFilter("ביצוע סינון", "ביצוע סינון", "קריירה פילטר")
    handleClose()
    setState(interimState)
  }

  const handleClear = () => {
    clearAll()
    jobFilter("איפוס", "איפוס", "קריירה פילטר")
  }

  return (
    <StyledModal open={open} onClose={handleClose}>
      <Container>
        <Header>
          <CloseButton onClick={handleClose}>
            <XIcon color={theme.colors.black} />
          </CloseButton>
          <TitleContainer>
            <StyledTitleText
              component="h1"
              desktopFontSize={40}
              mobileFontSize={40}
              desktopFontWeight={500}
              color={theme.colors.black}
            >
              {messages.filters || "מסננים"}
            </StyledTitleText>
          </TitleContainer>
        </Header>
        <Content>
          <AccordionFilter
            label={messages.jobType || "תחום"} // Fallback label
            icon={<StyledScopeIcon $color={theme.colors.newBlack} />}
            filterKey="professionIds"
            filters={filters.avialableProfessions}
            state={interimState}
            setState={setInterimState}
          />
          <AccordionFilter
            label={messages.location || "אזור המשרה"}
            icon={<StyledLocationIcon $color={theme.colors.newBlack} />}
            filterKey="areaIds"
            filters={filters.avialableAreas}
            state={interimState}
            setState={setInterimState}
          />
          {companyDivisionId.length === 0 && (
            <AccordionFilter
              label={messages.company || "חברה"}
              icon={<StyledCompanyIcon $color={theme.colors.newBlack} />}
              filterKey="companyIds"
              filters={filters.avialableCompanies}
              state={interimState}
              setState={setInterimState}
            />
          )}
        </Content>
        <Footer>
          <FooterButton onClick={handleClear} variant="secondary">
            {messages.clearAll || "ניקוי הכל"}
          </FooterButton>
          <FooterButton minWidth="200px" onClick={applyFilters} disabled={!hasChanges}>
            {messages.searchButtonLabel || "חיפוש"}
          </FooterButton>
        </Footer>
      </Container>
    </StyledModal>
  )
}
